import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type Auth,
  type User
} from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL, type FirebaseStorage } from "firebase/storage";
import type { RuntimeConfig } from "./api";

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let storage: FirebaseStorage | null = null;

export function configureFirebase(config: RuntimeConfig["firebase"]) {
  if (!config.apiKey || !config.projectId || !config.appId) return null;
  if (!app) {
    app = initializeApp(config);
    auth = getAuth(app);
    storage = getStorage(app);
  }
  return app;
}

export function getFirebaseAuth() {
  return auth;
}

export function currentUser(): User | null {
  return auth?.currentUser ?? null;
}

export async function signInWithGoogle() {
  if (!auth) throw new Error("Firebase Auth is not configured");
  const provider = new GoogleAuthProvider();
  try {
    await signInWithPopup(auth, provider);
  } catch (error) {
    const code = typeof error === "object" && error && "code" in error ? String(error.code) : "";
    if (code === "auth/popup-blocked" || code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
      await signInWithRedirect(auth, provider);
      return;
    }
    throw error;
  }
}

export async function completeRedirectSignIn() {
  if (!auth) return;
  await getRedirectResult(auth);
}

export async function signOutUser() {
  if (auth) await signOut(auth);
}

export async function getIdToken() {
  return auth?.currentUser?.getIdToken();
}

export async function uploadPlacementPhoto(posterId: string, file: File) {
  if (!storage) return undefined;
  const extension = file.name.split(".").pop() || "jpg";
  const target = ref(storage, `placements/${posterId}/${crypto.randomUUID()}.${extension}`);
  await uploadBytes(target, file, { contentType: file.type });
  return getDownloadURL(target);
}
